import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Award, Building2, Layers, Trophy, Users, X } from 'lucide-react';

interface ReferentielDirecteurCompteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'piliers' | 'progression';

type Pillar = {
  title: string;
  items: string[];
};

const PILLARS: Pillar[] = [
  {
    title: 'Discours de ventes',
    items: [
      'Maîtrise du discours de vente',
      'Gestion des objections',
      'Négociations de prix',
      'Présence aux événements',
      'Maîtrise du discours d’ascenseur',
    ],
  },
  {
    title: 'Gestion de la relation client',
    items: [
      'Ambassadeur BZ',
      'Gestion des plaintes',
      'Taux de satisfaction client de X %',
      'Participation active aux comités TI',
      'Élaboration de stratégies de relances',
      'Mise en place efficace des stratégies de relance',
      'Gestion documentaire',
    ],
  },
  {
    title: 'Gestion documentaire',
    items: [
      'Qualité documentaire',
      'Qualité de la prise de besoins',
      'Qualité des recommandations',
      'Capacité à faire des ventes complémentaires',
      'Peu de correctifs à faire lors de la préparation de vos ODS',
    ],
  },
  {
    title: "Impact sur l’équipe",
    items: [
      'Implications BZ (social, comités internes, etc.)',
      'Coaching aux collègues',
      'Ambassadeur',
      'Allure professionnelle',
      'Humeur positive au travail',
    ],
  },
  {
    title: 'Performance',
    items: [
      'Nombre de comptes clients',
      'Montant global des comptes clients',
      'Possède X nombre de comptes majeurs',
      '% de croissance client',
      'Efficacité générale',
      'Proactivité dans le plan d’action',
    ],
  },
  {
    title: 'Innovation',
    items: [
      'Trouve des nouvelles idées',
      'Mets en place des nouvelles idées',
      'Accepte et promeut le changement',
    ],
  },
  {
    title: 'Développement',
    items: [
      'Participe à un groupe de réseautage',
      'Amène des opportunités à travers son réseau personnel',
      'Génère des références à partir de ses clients',
      'Réussit à transformer une opportunité en plusieurs opportunités',
    ],
  },
];

const DISCOURS_DE_VENTES_DETAILS: Record<string, { description: string }> = {
  'Maîtrise du discours de vente': {
    description:
      "Capacité à présenter la valeur de BZ et de PRIsME avec clarté, structure et impact. Le discours est cohérent, adapté au contexte du client et orienté sur les résultats d’affaires.",
  },
  'Gestion des objections': {
    description:
      "Aisance à accueillir les objections sans se justifier, à clarifier le vrai frein, puis à répondre avec des faits, des exemples et des options. L’objectif est de faire avancer la décision, pas de “gagner” un débat.",
  },
  'Négociations de prix': {
    description:
      "Sait négocier en protégeant la valeur (et la marge) tout en restant équitable. Utilise des leviers (portée, cadence, options) plutôt que de réduire le prix par réflexe.",
  },
  'Présence aux événements': {
    description:
      "Présence active et intentionnelle aux événements (réseautage, conférences, comités). Capacité à créer des liens, identifier des opportunités et faire un suivi concret après l’événement.",
  },
  'Maîtrise du discours d’ascenseur': {
    description:
      'Pitch court (15–30 secondes) qui explique clairement qui on est, ce qu’on fait et la valeur pour le client. Simple, mémorable, sans jargon, et naturel.',
  },
};

const PILLAR_DETAILS_BY_TITLE: Record<string, Record<string, { description: string }>> = {
  'Discours de ventes': DISCOURS_DE_VENTES_DETAILS,
  'Gestion de la relation client': {
    'Ambassadeur BZ': {
      description:
        'Incarne la marque BZ dans chaque interaction client. Projette confiance, crédibilité et professionnalisme, et renforce la perception de valeur de nos services.',
    },
    'Gestion des plaintes': {
      description:
        'Traite les insatisfactions rapidement et avec empathie. Documente les faits, propose un plan de correction, et s’assure que le client constate une amélioration concrète.',
    },
    'Taux de satisfaction client de X %': {
      description:
        'Maintient (ou améliore) un niveau de satisfaction cible mesurable. Suit les indicateurs, identifie les irritants récurrents et met en place des actions préventives.',
    },
    'Participation active aux comités TI': {
      description:
        'Participe de façon structurée aux comités TI (agenda, décisions, actions). S’assure que les enjeux d’affaires sont traduits en priorités et engagements clairs.',
    },
    'Élaboration de stratégies de relances': {
      description:
        'Définit une stratégie de relance adaptée au client (objectifs, messages, timing). Priorise les actions à forte valeur et évite les suivis “automatiques” sans intention.',
    },
    'Mise en place efficace des stratégies de relance': {
      description:
        'Exécute la relance avec rigueur (suivis, preuves, prochaines étapes). Mesure le résultat, ajuste le discours et sécurise des engagements (rencontre, décision, action).',
    },
    'Gestion documentaire': {
      description:
        'Assure une documentation client propre, à jour et facilement retrouvable (ODS, comptes rendus, décisions, actions). La qualité documentaire réduit les frictions et accélère l’exécution.',
    },
  },
  'Gestion documentaire': {
    'Qualité documentaire': {
      description:
        'Produit des documents clairs, structurés et sans ambiguïté. Le contenu est actionnable, cohérent, et respecte les standards BZ (mise en page, langage, précision).',
    },
    'Qualité de la prise de besoins': {
      description:
        'Mène une prise de besoins complète (contexte, objectifs, contraintes, risques). Valide la compréhension avec le client et évite les suppositions.',
    },
    'Qualité des recommandations': {
      description:
        'Propose des recommandations justifiées (bénéfices, impacts, coûts, risques). Présente des options et guide la décision avec une logique d’affaires.',
    },
    'Capacité à faire des ventes complémentaires': {
      description:
        'Détecte les opportunités pertinentes (upsell/cross-sell) qui augmentent la valeur pour le client. Présente l’option au bon moment, avec un lien clair au besoin.',
    },
    'Peu de correctifs à faire lors de la préparation de vos ODS': {
      description:
        'Prépare des ODS de qualité dès la première version. Anticipe les questions, respecte les standards et réduit les allers-retours de correction.',
    },
  },
  "Impact sur l’équipe": {
    'Implications BZ (social, comités internes, etc.)': {
      description:
        'Contribue activement à la vie interne (comités, initiatives, culture). Participe avec constance et apporte une valeur réelle, pas seulement une présence.',
    },
    'Coaching aux collègues': {
      description:
        'Partage ses apprentissages et aide les collègues à progresser (préparation, pitch, gestion client). Donne du feedback utile, concret et bienveillant.',
    },
    Ambassadeur: {
      description:
        'Représente BZ positivement à l’interne et à l’externe. Favorise l’alignement, protège la réputation de l’équipe, et agit comme référence de bonnes pratiques.',
    },
    'Allure professionnelle': {
      description:
        'Maintient une présentation et une posture professionnelle en tout temps (écrits, réunions, rencontres). Inspire confiance et crédibilité.',
    },
    'Humeur positive au travail': {
      description:
        'Contribue à un climat sain et motivant. Gère la pression avec maturité, évite la négativité contagieuse et favorise la collaboration.',
    },
  },
  Performance: {
    'Nombre de comptes clients': {
      description:
        'Gère un portefeuille de comptes adapté à sa capacité. Assure un suivi régulier et maintient une qualité de service constante sur l’ensemble des comptes.',
    },
    'Montant global des comptes clients': {
      description:
        'Gère la valeur totale du portefeuille (revenu / marge) avec une vision stratégique. Priorise les actions qui protègent et augmentent la valeur des comptes.',
    },
    'Possède X nombre de comptes majeurs': {
      description:
        'Sait gérer des comptes majeurs (complexité, parties prenantes, attentes élevées). Maintient une gouvernance, une communication et un plan d’action solides.',
    },
    '% de croissance client': {
      description:
        'Fait croître la relation client de manière durable (nouvelles initiatives, expansion, fidélisation). Mesure la progression et agit sur les leviers de croissance.',
    },
    'Efficacité générale': {
      description:
        'Travaille avec méthode et priorisation. Réduit les pertes de temps, anticipe les blocages et livre des résultats réguliers avec un minimum de friction.',
    },
    'Proactivité dans le plan d’action': {
      description:
        'Propose et met à jour un plan d’action clair (priorités, responsables, échéances). N’attend pas les urgences pour agir; anticipe et sécurise l’avancement.',
    },
  },
  Innovation: {
    'Trouve des nouvelles idées': {
      description:
        'Identifie de nouvelles approches pour améliorer la valeur client, l’efficacité interne ou la qualité des livrables. Les idées sont pertinentes et réalistes.',
    },
    'Mets en place des nouvelles idées': {
      description:
        'Transforme les idées en actions (pilotes, tests, déploiements). Mesure l’impact et ajuste rapidement pour obtenir des gains concrets.',
    },
    'Accepte et promeut le changement': {
      description:
        'Adopte les changements avec ouverture et encourage l’équipe à faire de même. Explique le “pourquoi”, réduit les résistances et facilite l’adoption.',
    },
  },
  Développement: {
    'Participe à un groupe de réseautage': {
      description:
        'S’implique dans des activités de réseautage pertinentes. Bâtit des relations, maintient une présence régulière et crée des opportunités à long terme.',
    },
    'Amène des opportunités à travers son réseau personnel': {
      description:
        'Active son réseau de façon naturelle et professionnelle. Fait émerger des opportunités qualifiées et initie des conversations menant à des rencontres.',
    },
    'Génère des références à partir de ses clients': {
      description:
        'Sait demander et obtenir des références au bon moment. Capitalise sur la satisfaction client pour ouvrir de nouvelles portes avec crédibilité.',
    },
    'Réussit à transformer une opportunité en plusieurs opportunités': {
      description:
        'Élargit une opportunité en identifiant d’autres besoins/équipes/filiales. Structure la découverte pour maximiser la valeur sans sur-vendre.',
    },
  },
};

type ProgressLevel = {
  level: 'Junior' | 'Intermédiaire' | 'Senior';
  color: string;
  bullets: string[];
};

const PROGRESSION: ProgressLevel[] = [
  {
    level: 'Junior',
    color: 'from-amber-500 to-orange-600',
    bullets: ['X piliers maîtrisés après 1 an', 'Profil du client : 15 usagers et moins'],
  },
  {
    level: 'Intermédiaire',
    color: 'from-blue-600 to-indigo-700',
    bullets: ['X piliers maîtrisés', 'Profil des clients : 15 usagers et – X %, 50 usagers et – X %'],
  },
  {
    level: 'Senior',
    color: 'from-emerald-600 to-green-700',
    bullets: ['X piliers maîtrisés', 'Profil des clients : 15 usagers et – X %, 50 usagers et – X %, 51 usagers + X %'],
  },
];

export default function ReferentielDirecteurCompteModal({ isOpen, onClose }: ReferentielDirecteurCompteModalProps) {
  const [tab, setTab] = useState<TabId>('piliers');
  const [expandedPillarTitle, setExpandedPillarTitle] = useState<string | null>(null);

  const totalItems = useMemo(() => PILLARS.reduce((sum, p) => sum + p.items.length, 0), []);
  const expandedPillar = useMemo(
    () => (expandedPillarTitle ? PILLARS.find((p) => p.title === expandedPillarTitle) : undefined),
    [expandedPillarTitle]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (expandedPillarTitle) setExpandedPillarTitle(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, expandedPillarTitle]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="rdc-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            role="button"
            tabIndex={-1}
            aria-label="Fermer"
          />

          <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
            <motion.div
              key="rdc-panel"
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-6xl w-full border border-gray-200 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col relative"
            >
              {/* Hero + actions (sticky) */}
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600" />
                <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.55),transparent_55%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.35),transparent_60%),radial-gradient(circle_at_40%_90%,rgba(255,255,255,0.35),transparent_60%)]" />

                <div className="relative px-6 pt-6 pb-5 text-white">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="relative shrink-0">
                        <div className="absolute inset-0 rounded-full bg-white/15 blur-[1px]" />
                        <div className="relative w-16 h-16 rounded-full overflow-hidden ring-4 ring-white/20 shadow-xl">
                          <img
                            src="/AG-transparent.png"
                            alt="Antoine Giroux"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.currentTarget;
                              img.style.display = 'none';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-black/10" />
                        </div>
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight truncate">
                          Référentiel directeur de compte
                        </h2>
                        <p className="text-white/85 text-sm sm:text-base">
                          Piliers de compétences • progression (Junior → Intermédiaire → Senior)
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="px-3 py-1 rounded-full bg-white/15 border border-white/20 text-xs">
                            {PILLARS.length} piliers
                          </span>
                          <span className="px-3 py-1 rounded-full bg-white/15 border border-white/20 text-xs">
                            {totalItems} critères
                          </span>
                          <span className="px-3 py-1 rounded-full bg-white/15 border border-white/20 text-xs inline-flex items-center gap-1">
                            <Trophy className="w-3.5 h-3.5" /> Alignement excellence
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={onClose}
                      className="p-2.5 hover:bg-white/10 rounded-xl transition-colors border border-white/20 bg-white/10"
                      type="button"
                      aria-label="Fermer"
                      title="Fermer (Esc)"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Tabs */}
                  <div className="mt-5 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setTab('piliers')}
                      className={`px-4 py-2 rounded-2xl border transition-all inline-flex items-center gap-2 text-sm font-semibold ${
                        tab === 'piliers'
                          ? 'bg-white text-slate-900 border-white shadow-lg shadow-black/10'
                          : 'bg-white/10 text-white border-white/20 hover:bg-white/15'
                      }`}
                    >
                      <Layers className="w-4 h-4" />
                      Piliers de compétences
                    </button>
                    <button
                      type="button"
                      onClick={() => setTab('progression')}
                      className={`px-4 py-2 rounded-2xl border transition-all inline-flex items-center gap-2 text-sm font-semibold ${
                        tab === 'progression'
                          ? 'bg-white text-slate-900 border-white shadow-lg shadow-black/10'
                          : 'bg-white/10 text-white border-white/20 hover:bg-white/15'
                      }`}
                    >
                      <Award className="w-4 h-4" />
                      Niveau de progression
                    </button>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 bg-gradient-to-b from-white to-slate-50 overflow-y-auto">
                {tab === 'piliers' ? (
                  <div className="space-y-5">
                    <div className="flex items-end justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-xs font-semibold text-slate-500">PILIERS DE COMPÉTENCES</div>
                        <h3 className="text-xl font-bold text-slate-900">Ce qu’on attend d’un DDC BZ</h3>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-slate-200">
                          <Building2 className="w-3.5 h-3.5" /> Relation client
                        </span>
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-slate-200">
                          <Users className="w-3.5 h-3.5" /> Impact équipe
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {PILLARS.map((p) => (
                        <div
                          key={p.title}
                          className={`rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden transition-shadow ${
                            'hover:shadow-md cursor-pointer'
                          }`}
                          role="button"
                          tabIndex={0}
                          aria-label={`Ouvrir le pilier ${p.title}`}
                          onClick={() => {
                            setExpandedPillarTitle(p.title);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') setExpandedPillarTitle(p.title);
                          }}
                        >
                          <div className="px-5 py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white">
                            <div className="font-extrabold tracking-tight">{p.title}</div>
                            <div className="text-white/70 text-xs mt-1">{p.items.length} critères</div>
                          </div>
                          <div className="p-5">
                            <ul className="space-y-2.5">
                              {p.items.map((it) => (
                                <li key={it} className="flex gap-3 text-sm text-slate-800 leading-snug">
                                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-orange-500 shrink-0" />
                                  <span>{it}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div>
                      <div className="text-xs font-semibold text-slate-500">NIVEAU DE PROGRESSION</div>
                      <h3 className="text-xl font-bold text-slate-900">Trajectoire de performance</h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Guide simple pour situer le niveau attendu selon l’expérience et le profil des clients.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {PROGRESSION.map((lvl) => (
                        <div
                          key={lvl.level}
                          className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                        >
                          <div className={`px-5 py-4 bg-gradient-to-r ${lvl.color} text-white`}>
                            <div className="text-lg font-extrabold">{lvl.level}</div>
                          </div>
                          <div className="p-5">
                            <ul className="space-y-2.5">
                              {lvl.bullets.map((b) => (
                                <li key={b} className="flex gap-3 text-sm text-slate-800 leading-snug">
                                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-900 shrink-0" />
                                  <span>{b}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Expanded pillar (start with Discours de ventes) */}
              <AnimatePresence>
                {tab === 'piliers' && expandedPillarTitle ? (
                  <motion.div
                    key="expanded-pillar"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                    className="absolute inset-0 bg-white/95 backdrop-blur-sm"
                  >
                    <div className="h-full flex flex-col">
                      <div className="shrink-0 px-6 py-4 border-b border-slate-200 bg-white">
                        <div className="flex items-center justify-between gap-4">
                          <button
                            type="button"
                            onClick={() => setExpandedPillarTitle(null)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 font-semibold"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            Retour
                          </button>
                          <div className="min-w-0 text-right">
                            <div className="text-xs font-semibold text-slate-500">PILIER</div>
                            <div className="text-lg font-extrabold text-slate-900 truncate">
                              {expandedPillarTitle}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-white to-slate-50">
                        <div className="rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                          <div className="px-5 py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white">
                            <div className="font-extrabold tracking-tight">Critères détaillés</div>
                            <div className="text-white/75 text-sm mt-1">
                              Descriptions pour bien définir chaque critère (version 1).
                            </div>
                          </div>
                          <div className="p-5 space-y-4">
                            {expandedPillar?.items.map((criterion) => {
                              const detail =
                                (expandedPillarTitle ? PILLAR_DETAILS_BY_TITLE[expandedPillarTitle] : undefined)?.[
                                  criterion
                                ] || undefined;
                              return (
                                <div key={criterion} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4">
                                  <div className="font-bold text-slate-900">{criterion}</div>
                                  <div className="text-sm text-slate-700 mt-1 leading-relaxed">
                                    {detail?.description || 'Description à compléter.'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

